import React, { useMemo } from "react";
import { CornerDownRight, FileText, GitBranch, Split } from "lucide-react";
import { TICKET_PRIORITY_LABELS } from "constants/tickets";

/**
 * FlowPreview (SOUND-506) — the read-only "what this generates" view. Because
 * the flow is a DAG (earlier-only deps), it renders as a layered indented tree:
 * depth = dependency level (0 = runs immediately; a step's depth = 1 + max(dep
 * depth)). Parallelism reads as multiple depth-0 steps; joins read as an
 * `▸ after: …` line listing the step's `depends_on` targets by title. Live —
 * it's the same in-memory model the editor mutates, so it's the plain-language
 * contract of what POST /tickets will instantiate.
 *
 * SOUND-546 — a step with `spawnWhen` shows a "Conditional" badge, plus its
 * condition in words when `conditionTextById[_id]` is given. `skippedIds`
 * (create modal: the intake answers so far do not meet the condition) renders
 * those steps muted as "skipped"; the caller has already dropped them from the
 * other steps' `dependsOn`, as the server does.
 */
export default function FlowPreview({
  requestTypeLabel,
  intakeFields = [],
  steps = [],
  functions = [],
  conditionTextById = {},
  skippedIds = [],
}) {
  const skipped = useMemo(() => new Set(skippedIds), [skippedIds]);
  const roleLabel = useMemo(() => {
    const map = {};
    functions.forEach((f) => (map[f.functionKey] = f.label));
    return (key) => (key ? map[key] || key : "Unassigned queue");
  }, [functions]);

  const titleById = useMemo(() => {
    const map = {};
    steps.forEach((s) => (map[s._id] = s.title || s.key || "step"));
    return map;
  }, [steps]);

  const depthById = useMemo(() => {
    const map = {};
    steps.forEach((s) => {
      const deps = s.dependsOn || [];
      map[s._id] = deps.length === 0 ? 0 : 1 + Math.max(...deps.map((d) => map[d] ?? 0));
    });
    return map;
  }, [steps]);

  const intakeSummary = intakeFields
    .map((f) => f.label || f.key)
    .filter(Boolean)
    .join(", ");

  return (
    <div className="space-y-3">
      {/* Parent */}
      <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/20 p-3">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <div className="min-w-0">
          <div className="font-medium text-foreground">
            {requestTypeLabel || "Ticket"} <span className="text-muted-foreground">· parent ticket</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {intakeSummary ? `Typed intake: ${intakeSummary}` : "No structured intake fields"}
          </div>
        </div>
      </div>

      {/* Steps */}
      {steps.length === 0 ? (
        <p className="pl-2 text-sm text-muted-foreground">
          No child tickets — a single {requestTypeLabel || "ticket"} is created.
        </p>
      ) : (
        <div className="space-y-1.5">
          {steps.map((s, i) => {
            const deps = s.dependsOn || [];
            const depth = depthById[s._id] || 0;
            const isSkipped = skipped.has(s._id);
            const conditionText = conditionTextById[s._id];
            return (
              <div
                key={s._id}
                className={`flex items-start gap-2 rounded-md border p-2.5 ${
                  isSkipped ? "border-dashed border-border bg-muted/20 opacity-60" : "border-border bg-card"
                }`}
                style={{ marginLeft: depth * 20 }}
              >
                {depth > 0 && <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand/15 text-[11px] font-semibold text-brand">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className={`truncate text-sm font-medium text-foreground ${isSkipped ? "line-through" : ""}`}>
                      {s.title || <span className="text-muted-foreground">Untitled step</span>}
                    </span>
                    {s.spawnWhen && (
                      <span className="badge badge-info inline-flex shrink-0 items-center gap-1">
                        <Split className="h-3 w-3" />
                        Conditional
                      </span>
                    )}
                    {isSkipped && <span className="badge badge-muted shrink-0">Skipped</span>}
                  </div>
                  {s.spawnWhen && conditionText && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {isSkipped ? "Not created — condition not met: " : "Only when "}
                      {conditionText}
                    </div>
                  )}
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <GitBranch className="h-3 w-3" />
                      {roleLabel(s.assigneeRole)}
                    </span>
                    <span>·</span>
                    <span>{TICKET_PRIORITY_LABELS[s.defaultPriority] || s.defaultPriority || "Normal"}</span>
                  </div>
                  {deps.length > 0 && !isSkipped ? (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span className="text-xs text-muted-foreground">▸ after:</span>
                      {deps.map((d) => (
                        <span key={d} className="badge badge-muted">
                          {titleById[d] || "step"}
                        </span>
                      ))}
                    </div>
                  ) : isSkipped ? null : (
                    <div className="mt-0.5 text-xs text-muted-foreground/80">runs immediately</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
